import { useState, useEffect, useCallback, useRef } from 'react';
import { get, isAPIError, getErrorMessage } from '../utils/api';
import type { StockCategoryFilter } from '../types/stockCategories';
import { normalizeStockOption } from '../utils/stockOptionFormatting';
import { localStorageUtil } from '../utils/localStorage';

// Fallback S&P 500 list in case API fails
const FALLBACK_SP500_OPTIONS = [
  '$AAPL: Apple Inc.',
  '$MSFT: Microsoft Corporation',
  '$GOOGL: Alphabet Inc. Class A',
  '$AMZN: Amazon.com Inc.',
  '$NVDA: NVIDIA Corporation',
  '$META: Meta Platforms Inc.',
  '$TSLA: Tesla Inc.',
  '$BRK.B: Berkshire Hathaway Inc. Class B',
  '$UNH: UnitedHealth Group Incorporated',
  '$LLY: Eli Lilly and Company',
  '$JPM: JPMorgan Chase & Co.',
  '$V: Visa Inc.',
  '$XOM: Exxon Mobil Corporation',
  '$MA: Mastercard Incorporated',
  '$PG: Procter & Gamble Company',
  '$HD: Home Depot Inc.',
  '$JNJ: Johnson & Johnson',
  '$ABBV: AbbVie Inc.',
  '$AVGO: Broadcom Inc.',
  '$MRK: Merck & Co. Inc.',
  '$COST: Costco Wholesale Corporation',
  '$ORCL: Oracle Corporation',
  '$KO: Coca-Cola Company',
  '$CVX: Chevron Corporation',
  '$WMT: Walmart Inc.',
  '$BAC: Bank of America Corporation',
  '$PEP: PepsiCo Inc.',
  '$MCD: McDonald\'s Corporation',
  '$CSCO: Cisco Systems Inc.',
  '$TMO: Thermo Fisher Scientific Inc.',
  '$ABT: Abbott Laboratories',
  '$CRM: Salesforce Inc.',
  '$ACN: Accenture plc',
  '$WFC: Wells Fargo & Company',
  '$DIS: Walt Disney Company',
  '$NFLX: Netflix Inc.',
  '$ADBE: Adobe Inc.',
  '$DHR: Danaher Corporation',
  '$VZ: Verizon Communications Inc.',
  '$TXN: Texas Instruments Incorporated',
  '$NEE: NextEra Energy Inc.',
  '$PM: Philip Morris International Inc.',
  '$RTX: Raytheon Technologies Corporation',
  '$UNP: Union Pacific Corporation',
  '$COP: ConocoPhillips',
  '$T: AT&T Inc.',
  '$INTU: Intuit Inc.',
  '$CAT: Caterpillar Inc.',
  '$LOW: Lowe\'s Companies Inc.'
];

interface UseCategoryStockOptionsReturn {
  options: string[];
  marketCaps: Record<string, number>;
  loading: boolean;
  error: string | null;
  searchOptions: (query: string) => Promise<string[]>;
}

interface CategoryCacheEntry {
  options: string[];
  marketCaps: Record<string, number>;
  timestamp: number;
}

interface StoredStockOptions {
  version: number;
  options: string[];
  marketCaps: Record<string, number>;
  timestamp: number;
}

const STOCK_OPTIONS_CACHE_TTL = 1000 * 60 * 60 * 24; // 24 hours, reset daily
const STOCK_OPTIONS_STORAGE_PREFIX = 'wallstreetle_stock_options_';
const STOCK_OPTIONS_STORAGE_VERSION = 2;
const inMemoryCache = new Map<StockCategoryFilter, CategoryCacheEntry>();
const inFlightOptionFetches = new Map<StockCategoryFilter, Promise<{ options: string[]; marketCaps: Record<string, number> }>>();

const getStorageKey = (category: StockCategoryFilter): string =>
  `${STOCK_OPTIONS_STORAGE_PREFIX}${category}`;

const getDateKey = (timestamp: number): string | null => {
  if (!Number.isFinite(timestamp)) {
    return null;
  }

  try {
    return new Date(timestamp).toISOString().split('T')[0];
  } catch {
    return null;
  }
};

const isStaleTimestamp = (timestamp: number): boolean => {
  const now = Date.now();
  const storedDay = getDateKey(timestamp);
  const today = getDateKey(now);

  if (!storedDay || !today) {
    return true;
  }

  if (storedDay !== today) {
    return true;
  }

  return now - timestamp > STOCK_OPTIONS_CACHE_TTL;
};

const removeStoredOptions = (category: StockCategoryFilter): void => {
  localStorageUtil.removeItem(getStorageKey(category));
};

const getCachedOptions = (category: StockCategoryFilter): CategoryCacheEntry | null => {
  const entry = inMemoryCache.get(category);
  if (!entry) {
    return null;
  }

  if (entry.options.length === 0) {
    inMemoryCache.delete(category);
    removeStoredOptions(category);
    return null;
  }

  if (isStaleTimestamp(entry.timestamp)) {
    inMemoryCache.delete(category);
    removeStoredOptions(category);
    return null;
  }

  return entry;
};

const loadOptionsFromStorage = (category: StockCategoryFilter): CategoryCacheEntry | null => {
  const stored = localStorageUtil.getItem<StoredStockOptions>(getStorageKey(category));
  if (!stored) {
    return null;
  }

  if (
    stored.version !== STOCK_OPTIONS_STORAGE_VERSION ||
    !Array.isArray(stored.options) ||
    typeof stored.timestamp !== 'number'
  ) {
    removeStoredOptions(category);
    return null;
  }

  if (isStaleTimestamp(stored.timestamp)) {
    removeStoredOptions(category);
    return null;
  }

  if (!Array.isArray(stored.options) || stored.options.length === 0) {
    removeStoredOptions(category);
    return null;
  }

  return {
    options: stored.options,
    marketCaps: stored.marketCaps || {},
    timestamp: stored.timestamp
  };
};

const persistOptionsToMemory = (category: StockCategoryFilter, options: string[], marketCaps: Record<string, number> = {}, timestamp = Date.now()): void => {
  inMemoryCache.set(category, { options, marketCaps, timestamp });
};

const persistOptionsToStorage = (category: StockCategoryFilter, options: string[], marketCaps: Record<string, number> = {}, timestamp = Date.now()): void => {
  localStorageUtil.setItem<StoredStockOptions>(getStorageKey(category), {
    version: STOCK_OPTIONS_STORAGE_VERSION,
    options,
    marketCaps,
    timestamp
  });
};

const persistOptionsToCache = (
  category: StockCategoryFilter,
  options: string[],
  marketCaps: Record<string, number> = {},
  timestamp = Date.now()
): void => {
  if (!options || options.length === 0) {
    removeStoredOptions(category);
    inMemoryCache.delete(category);
    return;
  }

  persistOptionsToMemory(category, options, marketCaps, timestamp);
  persistOptionsToStorage(category, options, marketCaps, timestamp);
};

const hydrateCachedOptions = (category: StockCategoryFilter): CategoryCacheEntry | null => {
  const cached = getCachedOptions(category);
  if (cached) {
    return cached;
  }

  const stored = loadOptionsFromStorage(category);
  if (stored) {
    persistOptionsToMemory(category, stored.options, stored.marketCaps, stored.timestamp);
    return stored;
  }

  return null;
};

const formatOptions = (options: string[]): string[] =>
  options
    .map((option) => normalizeStockOption(option).display)
    .filter((value) => Boolean(value && value.trim()));

const fetchCategoryOptions = (category: StockCategoryFilter): Promise<{ options: string[]; marketCaps: Record<string, number> }> => {
  const existingPromise = inFlightOptionFetches.get(category);
  if (existingPromise) {
    console.log(`♻️ Reusing in-flight ${category} options request`);
    return existingPromise;
  }

  const fetchPromise = (async () => {
    console.log(`🔍 Fetching ${category} stock options from /api/stocks/categories/${category}/options...`);
    const response = await get<{ options: string[]; marketCaps?: Record<string, number>; category: string; count: number }>(
      `/api/stocks/categories/${category}/options`
    );
    console.log('📊 Stock options API response:', response);

    const optionsData = response.data?.options;

    if (response.success && optionsData) {
      console.log(`✅ Raw options received for ${category}:`, optionsData.length, 'items');
      console.log('📄 Sample options:', optionsData.slice(0, 3));
      return {
        options: formatOptions(optionsData),
        marketCaps: response.data?.marketCaps ?? {}
      };
    }

    console.error('❌ Invalid response format:', response);
    throw new Error('Invalid response format');
  })();

  inFlightOptionFetches.set(category, fetchPromise);

  fetchPromise.finally(() => {
    if (inFlightOptionFetches.get(category) === fetchPromise) {
      inFlightOptionFetches.delete(category);
    }
  });

  return fetchPromise;
};

/**
 * Custom hook for fetching stock options for a specific category
 * @param category - The stock category to fetch options for (defaults to 'spy')
 */
export function useCategoryStockOptions(category: StockCategoryFilter = 'spy'): UseCategoryStockOptionsReturn {
  const [options, setOptions] = useState<string[]>([]);
  const [marketCaps, setMarketCaps] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache for search results (per category)
 const [searchCache, setSearchCache] = useState<Map<string, string[]>>(new Map());

  const requestIdRef = useRef(0);
  const activeRequestRef = useRef<{ category: StockCategoryFilter; id: number } | null>(null);

  const isLatestRequest = useCallback(
    (requestCategory: StockCategoryFilter, requestId: number): boolean => {
      const activeRequest = activeRequestRef.current;
      return !!activeRequest && activeRequest.category === requestCategory && activeRequest.id === requestId;
    },
    []
  );

  const hydrateFromCache = useCallback((): boolean => {
    const cached = hydrateCachedOptions(category);
    if (!cached) {
      return false;
    }

    setOptions(cached.options);
    setMarketCaps(cached.marketCaps);
    setLoading(false);
    setSearchCache(prev => {
      const next = new Map(prev);
      next.set(`${category}:`, cached.options);
      return next;
    });
    return true;
  }, [category]);

  // Fetch all options when cache is empty/stale
  const fetchAllOptions = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    activeRequestRef.current = { category, id: requestId };

    setLoading(true);
    setError(null);

    try {
      const result = await fetchCategoryOptions(category);

      if (!isLatestRequest(category, requestId)) {
        console.log(`⚠️ Ignoring stale stock options response for ${category}`);
        return;
      }

      setOptions(result.options);
      setMarketCaps(result.marketCaps);
      persistOptionsToCache(category, result.options, result.marketCaps);

      // Cache the full list (category-specific cache key)
      setSearchCache(prev => {
        const next = new Map(prev);
        next.set(`${category}:`, result.options);
        return next;
      });
      console.log(`💾 Cached ${result.options.length} ${category} options`);
    } catch (err) {
      if (!isLatestRequest(category, requestId)) {
        console.log(`⚠️ Ignoring error from stale stock options request for ${category}`);
        return;
      }

      const errorMessage = isAPIError(err) ? getErrorMessage(err) : 'Failed to fetch S&P 500 options';
      setError(errorMessage);
      console.error('❌ Error fetching S&P 500 options:', err);
      console.error('🔧 Error details:', {
        isAPIError: isAPIError(err),
        message: errorMessage,
        err
      });

      // Use fallback data if API fails (only for SPY category)
      if (category === 'spy') {
        console.log('🔄 Using fallback S&P 500 options...');
        const fallbackFormatted = formatOptions(FALLBACK_SP500_OPTIONS);
        setOptions(fallbackFormatted);
        setMarketCaps({});
        persistOptionsToCache(category, fallbackFormatted, {});
        setSearchCache(prev => {
          const next = new Map(prev);
          next.set(`${category}:`, fallbackFormatted);
          return next;
        });
        console.log('✅ Loaded', fallbackFormatted.length, 'fallback options');
      } else {
        console.log(`⚠️ No fallback data available for ${category} category`);
      }
    } finally {
      if (isLatestRequest(category, requestId)) {
        setLoading(false);
      }
    }
  }, [category, isLatestRequest]);

  // Search options with query
  const searchOptions = useCallback(async (query: string): Promise<string[]> => {
    const normalizedQuery = query.trim().toLowerCase();
    const cacheKey = `${category}:${normalizedQuery}`;

    // Check cache first
    if (searchCache.has(cacheKey)) {
      return searchCache.get(cacheKey)!;
    }

    // If we have all options loaded, filter locally
    if (options.length > 0) {
      const filtered = options.filter(option =>
        option.toLowerCase().includes(normalizedQuery)
      );
      setSearchCache(prev => {
        const next = new Map(prev);
        next.set(cacheKey, filtered);
        return next;
      });
      return filtered;
    }

    // Otherwise, fetch from API with query
    try {
      setError(null);
      const response = await get<{ options: string[]; category: string; count: number }>(`/api/stocks/categories/${category}/options?query=${encodeURIComponent(query)}`);

      // Handle response structure
      const optionsData = response.data?.options;

      if (response.success && optionsData) {
        // Backend already formats as "(SYMBOL) Company Name" strings
        // Cache the result
        const formatted = formatOptions(optionsData);
        setSearchCache(prev => {
          const next = new Map(prev);
          next.set(cacheKey, formatted);
          return next;
        });

        return formatted;
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      const errorMessage = isAPIError(err) ? getErrorMessage(err) : `Failed to search ${category} options`;
      setError(errorMessage);
      console.error(`Error searching ${category} options:`, err);
      return [];
    }
  }, [category, options, searchCache]);

  // Hydrate from cache and refresh from API when category changes
  useEffect(() => {
    setSearchCache(new Map());
    setError(null);

    const hydrated = hydrateFromCache();
    if (!hydrated) {
      setOptions([]);
      setMarketCaps({});
      setLoading(true);
      fetchAllOptions().catch((error) => {
        console.error(`Failed to load ${category} stock options`, error);
      });
    }
  }, [category, hydrateFromCache, fetchAllOptions]);

  useEffect(() => {
    return () => {
      activeRequestRef.current = null;
    };
  }, []);

  return {
    options,
    marketCaps,
    loading,
    error,
    searchOptions,
  };
}

export default useCategoryStockOptions;
